import { useCallback, useEffect, useRef, useState } from 'react';
import type { LocalVideoTrack } from 'livekit-client';
import { SmoothBackgroundProcessor } from './SmoothBackgroundProcessor';

export type VirtualBgKind = 'none' | 'blur' | 'image';

/**
 * Manages virtual backgrounds on a LocalVideoTrack using
 * @livekit/track-processors. Replaces the previous MediaPipe-based
 * implementation with the LiveKit-native processor (lighter, official).
 *
 * The processors module is lazy-loaded (~1MB with WASM assets) only
 * when the doctor first toggles a background.
 *
 * Usage:
 *   const videoTrack = localParticipant?.getTrackPublication(Track.Source.Camera)?.videoTrack;
 *   const { kind, setBlur, setImage, clear, loading, error } = useVirtualBackgroundLK(videoTrack);
 */
export function useVirtualBackgroundLK(videoTrack: LocalVideoTrack | null) {
  const [kind, setKind] = useState<VirtualBgKind>('none');
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache the imported module to avoid re-imports on every toggle
  const moduleRef = useRef<{
    BackgroundProcessor: (opts: any) => unknown;
  } | null>(null);

  const loadModule = useCallback(async () => {
    if (moduleRef.current) return moduleRef.current;
    
    // We now use our custom SmoothBackgroundProcessor!
    moduleRef.current = {
      BackgroundProcessor: (opts: any) => new SmoothBackgroundProcessor(opts),
    };
    return moduleRef.current;
  }, []);

  const applyProcessor = useCallback(
    async (processor: unknown) => {
      if (!videoTrack) return;
      // bind: llamado suelto, LiveKit pierde `this` y falla en trackChangeLock
      const setProcessor = videoTrack.setProcessor.bind(videoTrack) as (p: unknown) => Promise<void>;
      await setProcessor(processor);
    },
    [videoTrack],
  );

  const setBlur = useCallback(
    async (radius = 10) => {
      if (!videoTrack) return;
      setLoading(true);
      setError(null);
      try {
        const mod = await loadModule();
        const processorOptions = {
          mode: 'blur',
          blurRadius: radius
        };
        await applyProcessor(mod.BackgroundProcessor(processorOptions));
        setKind('blur');
        setCurrentImage(null);
      } catch (err) {
        console.error('[useVirtualBackgroundLK] setBlur failed:', err);
        setError(err instanceof Error ? err.message : 'No se pudo aplicar el fondo borroso.');
      } finally {
        setLoading(false);
      }
    },
    [videoTrack, loadModule, applyProcessor],
  );

  const setImage = useCallback(
    async (imagePath: string) => {
      if (!videoTrack) return;
      setLoading(true);
      setError(null);
      try {
        const mod = await loadModule();
        const processorOptions = {
          mode: 'image',
          imagePath: imagePath
        };
        await applyProcessor(mod.BackgroundProcessor(processorOptions));
        setKind('image');
        setCurrentImage(imagePath);
      } catch (err) {
        console.error('[useVirtualBackgroundLK] setImage failed:', err);
        setError(err instanceof Error ? err.message : 'No se pudo aplicar el fondo virtual.');
      } finally {
        setLoading(false);
      }
    },
    [videoTrack, loadModule, applyProcessor],
  );

  const clear = useCallback(async () => {
    if (!videoTrack) return;
    setLoading(true);
    try {
      await videoTrack.stopProcessor();
      setKind('none');
      setCurrentImage(null);
    } catch (err) {
      console.error('[useVirtualBackgroundLK] clear failed:', err);
    } finally {
      setLoading(false);
    }
  }, [videoTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoTrack && kind !== 'none') {
        videoTrack.stopProcessor().catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoTrack]);

  return { kind, currentImage, loading, error, setBlur, setImage, clear };
}
